<template>
  <v-main>
    <v-container fluid>
      <!-- Header Card -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <v-row align="center">
                <v-col cols="12" md="8">
                  <h3 class="text-h5 font-weight-bold primary--text mb-2">
                    <v-icon left color="primary">mdi-translate</v-icon>
                    Languages Management
                  </h3>
                  <p class="text-subtitle-1 text--secondary mb-0">
                    Manage system languages and their configurations
                  </p>
                </v-col>
                <v-col cols="12" md="4" class="text-right">
                  <v-btn
                    color="primary"
                    :to="{ name: 'languages-create' }"
                    elevation="2"
                    rounded
                    class="mr-2"
                  >
                    <v-icon left>mdi-plus</v-icon>
                    Add Language
                  </v-btn>
                  <v-btn
                    color="success"
                    @click="exportToExcel"
                    elevation="2"
                    rounded
                    :loading="exporting"
                  >
                    <v-icon left>mdi-file-excel</v-icon>
                    Export
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Filters Card -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="search"
                    label="Search languages..."
                    prepend-inner-icon="mdi-magnify"
                    solo
                    rounded
                    clearable
                    hide-details
                    @input="searchLanguages"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="filters.is_active"
                    :items="statusOptions"
                    label="Status"
                    prepend-inner-icon="mdi-filter"
                    solo
                    rounded
                    clearable
                    hide-details
                    @change="applyFilters"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="3">
                  <v-switch
                    v-model="filters.show_deleted"
                    label="Show Deleted"
                    color="warning"
                    hide-details
                    @change="applyFilters"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="2" class="text-right">
                  <v-chip
                    v-if="hasActiveFilters"
                    color="primary"
                    text-color="white"
                    @click="clearFilters"
                  >
                    <v-icon left small>mdi-filter</v-icon>
                    {{ activeFilterCount }} Active
                    <v-icon right small>mdi-close</v-icon>
                  </v-chip>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl">
            <v-data-table
              :headers="headers"
              :items="languages"
              :options.sync="options"
              :server-items-length="totalItems"
              :loading="loading"
              fixed-header
              height="285"
              class="elevation-0"
              @click:row="viewLanguage"
            >
              <!-- Code column -->
              <template #[`item.code`]="{ item }">
                <v-chip
                  small
                  color="primary"
                  text-color="white"
                  class="font-weight-bold"
                >
                  {{ item.code }}
                </v-chip>
              </template>

              <!-- Name column -->
              <template #[`item.name`]="{ item }">
                <div>
                  <div class="font-weight-medium">{{ item.name }}</div>
                  <div v-if="item.native_name" class="text-caption text--secondary">
                    {{ item.native_name }}
                  </div>
                </div>
              </template>

              <!-- Status column -->
              <template #[`item.is_active`]="{ item }">
                <v-chip
                  :color="item.is_active ? 'success' : 'error'"
                  text-color="white"
                  small
                >
                  <v-icon left small>
                    {{ item.is_active ? 'mdi-check-circle' : 'mdi-close-circle' }}
                  </v-icon>
                  {{ item.is_active ? 'Active' : 'Inactive' }}
                </v-chip>
              </template>

              <!-- Deleted status column -->
              <template #[`item.is_deleted`]="{ item }">
                <v-chip
                  v-if="item.is_deleted"
                  color="warning"
                  text-color="white"
                  small
                >
                  <v-icon left small>mdi-delete</v-icon>
                  Deleted
                </v-chip>
                <span v-else class="text--secondary">—</span>
              </template>

              <!-- Actions column -->
              <template #[`item.actions`]="{ item }">
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="viewLanguage(item)">
                      <v-list-item-icon>
                        <v-icon>mdi-eye</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>View</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="editLanguage(item)">
                      <v-list-item-icon>
                        <v-icon>mdi-pencil</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Edit</v-list-item-title>
                    </v-list-item>
                    <v-divider></v-divider>
                    <v-list-item
                      v-if="!item.is_deleted"
                      @click="deleteLanguage(item)"
                      class="error--text"
                    >
                      <v-list-item-icon>
                        <v-icon color="error">mdi-delete</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Delete</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-else
                      @click="restoreLanguage(item)"
                      class="success--text"
                    >
                      <v-list-item-icon>
                        <v-icon color="success">mdi-restore</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Restore</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>

              <!-- No data -->
              <template v-slot:no-data>
                <empty-state icon="mdi-translate-off" title="No languages found" :message="hasActiveFilters ? 'Try adjusting your filters' : 'Create your first language to get started'">
                  <v-btn
                  v-if="!hasActiveFilters"
                  color="primary"
                  :to="{ name: 'languages-create' }"
                  class="mt-4"
                  >
                  <v-icon left>mdi-plus</v-icon>
                  Add Language
                  </v-btn>
                </empty-state>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500px">
      <v-card>
        <v-card-title class="text-h5">
          <v-icon left color="error">mdi-delete</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete the language "{{ selectedLanguage?.name }}"?
          This action can be undone by restoring the language.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn
            color="error"
            text
            @click="confirmDelete"
            :loading="deleting"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000" top>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-main>
</template>

<script>
import axios from 'axios'

export default {
  name: 'LanguagesIndex',
  data() {
    return {
      languages: [],
      loading: false,
      exporting: false,
      deleting: false,
      search: '',
      totalItems: 0,
      options: {
        page: 1,
        itemsPerPage: 15,
        sortBy: ['name'],
        sortDesc: [false]
      },
      filters: {
        is_active: null,
        show_deleted: false
      },
      headers: [
        {
          text: 'Code',
          value: 'code',
          width: '100px'
        },
        {
          text: 'Name',
          value: 'name',
          sortable: true
        },
        {
          text: 'Status',
          value: 'is_active',
          width: '120px',
          sortable: false
        },
        {
          text: 'Deleted',
          value: 'is_deleted',
          width: '100px',
          sortable: false
        },
        {
          text: 'Actions',
          value: 'actions',
          width: '80px',
          sortable: false
        }
      ],
      statusOptions: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false }
      ],
      deleteDialog: false,
      selectedLanguage: null,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      searchTimeout: null
    }
  },
  computed: {
    hasActiveFilters() {
      return this.search || 
             this.filters.is_active !== null || 
             this.filters.show_deleted
    },
    activeFilterCount() {
      let count = 0
      if (this.search) count++
      if (this.filters.is_active !== null) count++
      if (this.filters.show_deleted) count++
      return count
    }
  },
  watch: {
    options: {
      handler() {
        this.fetchLanguages()
      },
      deep: true
    }
  },
  mounted() {
    this.fetchLanguages()
  },
  methods: {
    async fetchLanguages() {
      this.loading = true
      try {
        const params = {
          page: this.options.page,
          rowsPerPage: this.options.itemsPerPage,
          search_keyword: this.search || undefined,
          is_active: this.filters.is_active,
          show_deleted: this.filters.show_deleted ? 1 : 0
        }

        const response = await axios.get('/api/languages', { params })
        
        if (response.data.success) {
          this.languages = response.data.data
          this.totalItems = response.data.count
        } else {
          this.showSnackbar('Failed to fetch languages', 'error')
        }
      } catch (error) {
        console.error('Error fetching languages:', error)
        this.showSnackbar('Error fetching languages', 'error')
      } finally {
        this.loading = false
      }
    },
    searchLanguages() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.options.page = 1
        this.fetchLanguages()
      }, 500)
    },
    applyFilters() {
      this.options.page = 1
      this.fetchLanguages()
    },
    clearFilters() {
      this.search = ''
      this.filters.is_active = null
      this.filters.show_deleted = false
      this.applyFilters()
    },
    viewLanguage(language) {
      this.$router.push({ name: 'languages-edit', params: { id: language.id } })
    },
    editLanguage(language) {
      this.$router.push({ name: 'languages-edit', params: { id: language.id } })
    },
    deleteLanguage(language) {
      this.selectedLanguage = language
      this.deleteDialog = true
    },
    async confirmDelete() {
      if (!this.selectedLanguage) return
      
      this.deleting = true
      try {
        const response = await axios.post(`/api/languages/delete/${this.selectedLanguage.id}`)
        
        if (response.data.success) {
          this.showSnackbar('Language deleted successfully', 'success')
          this.fetchLanguages()
        } else {
          this.showSnackbar('Failed to delete language', 'error')
        }
      } catch (error) {
        console.error('Error deleting language:', error)
        this.showSnackbar('Error deleting language', 'error')
      } finally {
        this.deleting = false
        this.deleteDialog = false
        this.selectedLanguage = null
      }
    },
    async restoreLanguage(language) {
      try {
        const response = await axios.post(`/api/languages/restore/${language.id}`)
        
        if (response.data.success) {
          this.showSnackbar('Language restored successfully', 'success')
          this.fetchLanguages()
        } else {
          this.showSnackbar('Failed to restore language', 'error')
        }
      } catch (error) {
        console.error('Error restoring language:', error)
        this.showSnackbar('Error restoring language', 'error')
      }
    },
    async exportToExcel() {
      this.exporting = true
      try {
        const params = {
          search_keyword: this.search || undefined,
          is_active: this.filters.is_active,
          show_deleted: this.filters.show_deleted ? 1 : 0
        }

        const response = await axios.get('/api/languages/excel', {
          params,
          responseType: 'blob'
        })
        
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `languages_${new Date().toISOString().split('T')[0]}.xlsx`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        this.showSnackbar('Languages exported successfully', 'success')
      } catch (error) {
        console.error('Error exporting languages:', error)
        this.showSnackbar('Error exporting languages', 'error')
      } finally {
        this.exporting = false
      }
    },
    showSnackbar(text, color = 'success') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    }
  }
}
</script>

<style scoped>
.v-data-table >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
  cursor: pointer;
}

.v-data-table >>> .v-data-table__wrapper {
  border-radius: 12px;
}

.v-chip {
  font-size: 0.75rem;
}
</style>